import { useCallback, useState } from "react";
import { userIsPublic } from "~/domains/user/utils/roles";
import { trackSrpAddToWishlist } from "~/app/analytics/ga/ga";
import { type BaseProductData, type ProductData } from "~/domains/product/types/ProductData";
import type { OptionalPartDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { openAddToWishlistWizard } from "~/features/wishlist/components/modals/AddToWishlist/AddToWishlistService";
import { mapOptionalParts4Wishlist } from "~/features/wishlist/components/modals/AddToWishlist/utils";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { useDispatch } from "react-redux";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";
import { usePriceReferenceSelection } from "~/app/config/useSession";

export interface UseAddProductToWishlistFlowParams {
  productInput?: ProductData;
  initialQuantity?: number;
  initialPriceReference?: string;
  selectedOptionalParts?: Dict<OptionalPartDraft>;
}

export const useAddProductToWishlistFlow = ({
  productInput,
  initialQuantity = 1,
  initialPriceReference,
  selectedOptionalParts,
}: UseAddProductToWishlistFlowParams) => {
  const { selection, status } = usePriceReferenceSelection();
  const resolvedPriceReference = initialPriceReference ?? getInitialPriceReference(selection);
  // set a default product if it wasn't provided by productInput
  const [product, setProduct] = useState(productInput || { designation: "", code: "", isOptime: false });
  const dispatch = useDispatch();

  const onClick = useCallback(
    (productData?: BaseProductData) => {
      if (status !== "ready") {
        return;
      }
      // set new product by click if available
      if (productData) {
        setProduct(productData);
      }

      const data = productData || product;

      if (userIsPublic()) {
        dispatch(openAuthOverlay("register"));
      } else {
        trackSrpAddToWishlist(data.designation, 1);

        openAddToWishlistWizard({
          productCode: data.code,
          initialQuantity: initialQuantity,
          initialPriceReference: resolvedPriceReference,
          addTracking: (product) => trackSrpAddToWishlist(product.base.designation, 2),
          selectedOptionalParts: mapOptionalParts4Wishlist(data as ProductData, selectedOptionalParts),
        });
      }
    },
    [dispatch, initialQuantity, product, resolvedPriceReference, selectedOptionalParts, status],
  );

  const modal = false;

  return [onClick, modal] as const;
};
